import ActionTypes from '../actions/types'
import {CACHE_GlobalAdminOrganizationId} from '../constants/AppConstants'

/**
 * @typedef {Object} GlobalAdminState
 * @property {Object<string, Object>} buildingsLookup - Lookup of buildings by their IDs (for all buildings across the active orgs)
 * @property {Object<string, Object>} organizationsLookup - Lookup of organizations by their IDs
 * @property {Object<string, Object>} documentsLookup - Lookup of documents by their IDs
 * @property {Object<string, Array<string>>} documentsByBuilding - Mapping of building IDs to arrays of document IDs
 * @property {Object<string, Object>} staffLookup - Lookup of staff members by their IDs
 * @property {Object<string, Array<string>>} staffByBuilding - Mapping of building IDs to arrays of staff member IDs
 * @property {string|undefined} activeOrganizationId - Currently active organization ID
 */

/**
 * @type {GlobalAdminState}
 */
const initialState = {
  organizationsLookup: {},
  documentsLookup: {}, // this will have all documents across all organizations
  documentsByBuilding: {},
  buildingsLookup: {}, // this is similar to buildingsReducer.buildingsLookup except has all buildings in the active org
  staffLookup: {}, // this should only have staff for the active organization
  staffByBuilding: {}, // this should only have staff for the active organization
  activeOrganizationId: null,
}

// some state properties are org specific (like staff) so when we change orgs we reset to initial state
const resettableStateProperties = ['staffLookup', 'staffByBuilding', 'buildingsLookup']

// --------------------------------------------------------------------------------
// State permutation functions:

/**
 * @param {GlobalAdminState} state
 * @param {Array<{id: string, displayName: string, organizationId?: string, organization?: *}>} buildingsList
 * @returns {GlobalAdminState}
 */
function setBuildingsListForActiveOrg(state, buildingsList) {
  const nextBuildingsLookup = buildingsList.reduce((agr, b) => {
    agr[b.id] = b
    return agr
  }, {})

  return {
    ...state,
    buildingsLookup: nextBuildingsLookup,
  }
}

/**
 * This is used to determine what orgs we have access to and documents associations.
 * The buildingsList passed here are buildings that we - the user - have access to.
 * This differs from `setBuildingsListForActiveOrg` which uses all buildings in the org
 * not just those we're a "staff user" of.
 * @param {GlobalAdminState} state
 * @param {Array<{building: {id: string, displayName: string, organizationId?: string, organization?: *}}>} buildingsList
 * @return {GlobalAdminState}
 */
function setOrganizationLookupFromUsersBuildingsList(state, buildingsList) {
  const nextOrganizationsLookup = {}
  const nextDocumentsLookup = {}
  const nextDocumentsByBuilding = {}

  buildingsList.forEach(b => {
    if (!b.building.organizationId || !b.building.organization) {
      return
    }

    const orgId = b.building.organizationId
    const organization = b.building.organization

    // initialize documents array for this building if not already initialized
    nextDocumentsByBuilding[b.building.id] = nextDocumentsByBuilding[b.building.id] || []

    // if this is the first time we're encountering this orgId
    if (!nextOrganizationsLookup[orgId]) {
      // add it to the lookup
      nextOrganizationsLookup[orgId] = organization

      // process documents for this organization
      const documents = organization.documents || []
      documents.forEach(doc => {
        // add to documents lookup
        nextDocumentsLookup[doc.id] = doc

        // map documents to buildings
        const buildings = doc.buildings || []
        buildings.forEach(bConnection => {
          // initialize array if needed
          if (!nextDocumentsByBuilding[bConnection.buildingId]) {
            nextDocumentsByBuilding[bConnection.buildingId] = []
          }

          // add document ID to the building's document list
          nextDocumentsByBuilding[bConnection.buildingId].push(doc.id)
        })
      })
    }
  })

  return {
    ...state,
    organizationsLookup: nextOrganizationsLookup,
    documentsLookup: nextDocumentsLookup,
    documentsByBuilding: nextDocumentsByBuilding,
  }
}

/**
 * @param {GlobalAdminState} state
 * @param {string} organizationId
 * @return {GlobalAdminState}
 */
function setGlobalAdminOrganizationId(state, organizationId) {
  if (organizationId) {
    localStorage[CACHE_GlobalAdminOrganizationId] = organizationId
  } else {
    delete localStorage[CACHE_GlobalAdminOrganizationId]
  }

  return {
    ...state,
    activeOrganizationId: organizationId,
    // whenever we change orgs we reset certain state properties
    ...resettableStateProperties.reduce((agr, prop) => ({...agr, [prop]: initialState[prop]}), {}),
  }
}

/**
 * @param {GlobalAdminState} state
 * @param {{id: string}} document
 * @param {Array<string>} buildingIds
 * @returns {GlobalAdminState}
 */
function updateDocumentAndBuildings(state, document, buildingIds) {
  const nextDocumentsLookup = {...state.documentsLookup, [document.id]: document}
  const nextDocumentsByBuilding = Object.entries(state.documentsByBuilding).reduce((agr, [buildingId, documentIds]) => {
    const thisBuildingHasTheDocument = buildingIds.includes(buildingId)

    // start off with the document removed
    let documentsAtThisBuilding = documentIds.filter(d => d !== document.id)
    if (thisBuildingHasTheDocument) {
      // add it if needed
      documentsAtThisBuilding.push(document.id)
    }
    agr[buildingId] = documentsAtThisBuilding
    return agr
  }, {})
  return {
    ...state,
    documentsLookup: nextDocumentsLookup,
    documentsByBuilding: nextDocumentsByBuilding,
  }
}

/**
 * @param {GlobalAdminState} state
 * @param {string} documentId
 * @returns {GlobalAdminState}
 */
function removeDocumentAndBuildings(state, documentId) {
  const nextDocumentsLookup = {...state.documentsLookup}
  delete nextDocumentsLookup[documentId]

  const nextDocumentsByBuilding = Object.entries(state.documentsByBuilding).reduce((agr, [buildingId, documentIds]) => {
    agr[buildingId] = documentIds.filter(d => d !== documentId)
    return agr
  }, {})

  return {
    ...state,
    documentsLookup: nextDocumentsLookup,
    documentsByBuilding: nextDocumentsByBuilding,
  }
}

/**
 * @param {GlobalAdminState} state
 * @param {Array<{buildingId: string, staffId: string, staff: *}>} staffList
 * @return {GlobalAdminState}
 */
function setStaffFromSync(state, staffList) {
  const nextStaffLookup = {}
  const nextStaffByBuilding = {}

  staffList.forEach(bS => {
    nextStaffLookup[bS.staffId] = bS.staff

    // initialize array for this building if needed
    if (!nextStaffByBuilding[bS.buildingId]) {
      nextStaffByBuilding[bS.buildingId] = []
    }

    nextStaffByBuilding[bS.buildingId].push(bS.staffId)
  })

  return {
    ...state,
    staffLookup: nextStaffLookup,
    staffByBuilding: nextStaffByBuilding,
  }
}

/**
 * Updates organization ownerIds in state by adding or removing a staffId.
 * @param {GlobalAdminState} state - The current global admin state.
 * @param {string} organizationId - The ID of the organization to update.
 * @param {Array<string>} newOwnerIds - The new array of owner IDs for the organization.
 * @returns {GlobalAdminState} The updated state with new ownerIds for the organization.
 */
function updateOrganizationOwnerIdsInState(state, organizationId, newOwnerIds) {
  const org = state.organizationsLookup[organizationId]
  if (!org || !Array.isArray(org.ownerIds) || !newOwnerIds) {
    return state
  }

  return {
    ...state,
    organizationsLookup: {
      ...state.organizationsLookup,
      [organizationId]: {
        ...org,
        ownerIds: newOwnerIds,
      },
    },
  }
}

// --------------------------------------------------------------------------------

/**
 * @param {GlobalAdminState} state
 * @param {*} action
 * @return {GlobalAdminState}
 */
export default function (state = initialState, action) {
  switch (action.type) {
    case ActionTypes.BUILDINGS_UPDATE_ALL:
      return setOrganizationLookupFromUsersBuildingsList(state, action.payload)

    case ActionTypes.GLOBAL_ADMIN_SET_BUILDINGS_LIST:
      return setBuildingsListForActiveOrg(state, action.payload)

    case ActionTypes.GLOBAL_ADMIN_SET_ORGANIZATION_ID:
      return setGlobalAdminOrganizationId(state, action.payload)

    case ActionTypes.GLOBAL_ADMIN_UPDATE_DOCUMENT_AND_ASSOCIATED_BUILDINGS:
      return updateDocumentAndBuildings(state, action.payload.document, action.payload.buildingIds)

    case ActionTypes.GLOBAL_ADMIN_REMOVE_DOCUMENT_AND_ASSOCIATED_BUILDINGS:
      return removeDocumentAndBuildings(state, action.payload)

    case ActionTypes.GLOBAL_ADMIN_SET_STAFF:
      return setStaffFromSync(state, action.payload)

    case ActionTypes.GLOBAL_ADMIN_UPDATE_ORGANIZATION_OWNER_IDS:
      return updateOrganizationOwnerIdsInState(state, action.payload.organizationId, action.payload.newOwnerIds)
    default:
      return state
  }
}
